import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FormControl, InputLabel, MenuItem, Select, Typography, Stack, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';


interface SelectDurationDropdownProps {
  selected: number | null;
  setSelected: (e: number | null) => void;
  disabledFlag: boolean | undefined;
}

interface MeetingDurationObject {
  id: number;
  time: number | null;
  isCustom?: boolean;
}

const durationOptions: MeetingDurationObject[] = [
  {
    id: 1,
    time: 10,
  }, 
  {
    id: 2,
    time: 20,
  }, 
  {
    id: 3,
    time: 30,
  }, 
  {
    id: 4,
    time: 40,
  },
  {
    id: 5,
    time: 50,
  }, 
  {
    id: 6,
    time: 60,
  },
  {
    id: 7,
    time: 0,
    isCustom: true,
  },
];

const styles = {
  selectLabel: {
    '& .MuiFormLabel-root': {
      fontFamily: 'Schibsted Grotesk',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '12px',
      letterSpacing: '0em',
      textAlign: 'left',
      left: '0.1rem',
      color: '#878787',
      padding:'0px 0px 5px 8px',
    },
    '& .MuiSelect-select': {
      paddingTop: '16px!important',
    },
    '& .MuiFilledInput-input:focus': {
      backgroundColor: 'transparent',
    },
  },
  selectLabelHasValue: {
    '& .MuiFormLabel-root': {
      fontSize: '12px',
      color:'black',
    },
    '& .MuiFilledInput-input': {
      backgroundColor: 'white',
    },
    '& .MuiFilledInput-input:focus': {
      backgroundColor: 'white',
    },
    '& .MuiFilledInput-input:hover': {
      backgroundColor: 'white',
    },
  },
  dropdown: {
    padding: '0px 8px',
    borderRadius: '6px',
    backgroundColor: '#00000008',
  },
  dropdownHasValue: {
    '& .MuiFormLabel-root': {
      fontSize: '12px',
      color:'black',
    },
    backgroundColor: 'white!important',
    border:'1px solid rgba(0, 0, 0, 0.50)',
    '&:hover': {
      backgroundColor: 'white',
    },
  },
  menuItemText: {
    fontFamily: 'Schibsted Grotesk',
    fontsize: '14px',
    color: 'black',
    fontweight: 400,
    lineHeight: '17px',
    letterspacing: '0em',
    textalign: 'left',
  },
  customTextfieldStyle: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '12px',
    letterSpacing: '0em',
    textAlign: 'left',
    color: '#878787',
    padding:'8px 8px',
    borderRadius: '6px',
    backgroundColor: '#00000008',
    
  },
  customTextfieldStyleHasValue: {
    bgcolor: 'transparent',
    border:'1px solid rgba(0, 0, 0, 0.50)',
  },
};

const SelectDurationDropdown = ({ selected, setSelected, disabledFlag }: SelectDurationDropdownProps) => {
  const intl = useIntl();
  const [durationSelected, setDurationSelected] = useState<number | undefined>(); 

  useEffect(() => {
    const newDuration = durationOptions.find(option => option.time == selected)?.id;
    if (newDuration)
      setDurationSelected(newDuration);
    else if (selected && !newDuration) {
      setDurationSelected(7);
    }
  }, [selected]);

  const changeCustomDuration = (customValue: string) => {
    if (!customValue) return; 
    if (!isNaN(parseInt(customValue))) {
      if (parseInt(customValue) < 10)
        setSelected(null);
      else {
        setSelected(parseInt(customValue));
      }
    } else {
      setSelected(null);
    }
  };

  const onChangeSelectedHandler = (value: string) => {
    if (value == '7') {
      setDurationSelected(parseInt(value));
    } else {
      setSelected(durationOptions.find(option => `${option.id}` == value)?.time ?? 10);
    }

  };

  return (
    <Stack gap={1}>
      <FormControl
        disabled={disabledFlag ? true : false}
        fullWidth
        variant='filled'
        sx={{
          ...styles.selectLabel,
          ...(durationSelected && !isNaN(durationSelected) && styles.selectLabelHasValue),
        }}>
        {(!durationSelected || isNaN(durationSelected)) && <InputLabel id='duration-select-label'>
          <FormattedMessage defaultMessage='Select' />
        </InputLabel>}
        <Select
          labelId='duration-select-label'
          IconComponent={(props) => (
            <ExpandMoreIcon
              {...props}
              sx={{ width: '30px', height: '30px' }}
            />
          )}
          sx={{
            ...styles.dropdown,
            ...(durationSelected && !isNaN(durationSelected) && styles.dropdownHasValue ),
          }}
          disableUnderline = {true}
          value= {durationSelected ? `${durationSelected}` : ''}
          onChange={(e) => onChangeSelectedHandler(e.target.value)}>
          {durationOptions.length > 0 &&
          durationOptions?.map(({ id, time, isCustom }) => {
            return (
              <MenuItem key={id} value={id}>
                <Typography
                  sx={styles.menuItemText}
                  variant='body2'
                  className='role-menuitem'
                >
                  {!isCustom ? `${time} mins` : 'Custom'}
                </Typography>
              </MenuItem>
            );
          })
          };
        </Select>
      </FormControl>
      {durationSelected === 7 &&
        <>
          <Stack direction='row' gap={1}>
            <TextField
              variant='standard'
              InputProps={{ disableUnderline: true }}
              sx={{
                ...styles.customTextfieldStyle,
                ...(selected && styles.customTextfieldStyleHasValue),
                width: '100%',
              }}
              type='number'
              placeholder={intl.formatMessage({ defaultMessage: 'Enter Custom Duration Value' })}
              value={selected}
              onChange={(e) =>  changeCustomDuration(e.target.value)}
            />
            <TextField
              variant='standard'
              InputProps={{ disableUnderline: true }}
              sx={{
                ...styles.customTextfieldStyle,
                ...styles.customTextfieldStyleHasValue,
                maxWidth: 80,
              }}
              placeholder={intl.formatMessage({ defaultMessage: 'Enter Custom Duration Value' })}
              value='Minutes'
            />
          </Stack>
          {!selected &&
            <Typography variant='caption' color='error'> 
              * <FormattedMessage defaultMessage='Duration Value cannot be empty or below 10 minutes'/>
            </Typography>
          }
        </>
      }
    </Stack>
  );
};

export default SelectDurationDropdown;